import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { toggleDialog } from '../../store/control/duck';
import { injectIntl } from 'react-intl';
import { CircularProgress, Grid, TextField, Typography, Button, InputAdornment, IconButton } from '@mui/material';
import { openaiGenerateWithTextStreaming } from '../../store/openai/api';
import BottomSheetOrDialog from '../BottomSheetOrDialog/BottomSheetOrDialog';
import { handleError } from '../../utils/openai';
import './ChatBotDialog.scss';
import { withRouter } from 'react-router';
import { Markdown } from './markdown';
import { useMediaQuery } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { getTeamAiBotName } from '../../utils/teamAiSetting/teamAiSetting';

const ChatBotDialog = props => {
  const { intl, chatBotDialog, toggleDialog, promptIdentifier, mode, teamAiSettings } = props;

  const contentDivRef = useRef(null);
  const inputRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);

  // Add preset questions
  const presetQuestions = {
    team: [
      { id: 1, text: intl.formatMessage({ id: 'team-bot-preset-question-1' }) },
      { id: 2, text: intl.formatMessage({ id: 'team-bot-preset-question-2' }) },
      { id: 3, text: intl.formatMessage({ id: 'team-bot-preset-question-3' }) },
      { id: 4, text: intl.formatMessage({ id: 'team-bot-preset-question-4' }) },
      { id: 5, text: intl.formatMessage({ id: 'team-bot-preset-question-5' }) }
    ],
    general: [
      {
        id: 1,
        text: intl.formatMessage({ id: 'general-bot-preset-question-1' })
      },
      {
        id: 2,
        text: intl.formatMessage({ id: 'general-bot-preset-question-2' })
      },
      {
        id: 3,
        text: intl.formatMessage({ id: 'general-bot-preset-question-3' })
      }
    ]
  };

  const { closed } = chatBotDialog || {};

  const isMobile = useMediaQuery('(max-width: 600px)');

  const onChangeInputMessage = event => {
    setInputMessage(event.target.value);
  };

  const handlePresetQuestion = questionText => {
    setInputMessage(questionText);
    setTimeout(() => {
      sendMessage(questionText);
    }, 100);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (inputMessage && !loading) {
        sendMessage();
      }
    }
  };

  const clearConversation = useCallback(() => {
    setChatMessages([]);
    setFollowUpQuestions([]);
  }, []);

  const getFollowUpQuestionsCompleteAction = result => {
    try {
      const jsonResponse = JSON.parse(result);
      if (jsonResponse.followUpQuestions && Array.isArray(jsonResponse.followUpQuestions)) {
        setFollowUpQuestions(jsonResponse.followUpQuestions);
      }
    } catch (parseError) {
      console.error('Error parsing follow-up questions JSON:', parseError);
    }
  };

  const getFollowUpQuestions = useCallback(async () => {
    try {
      // Get the last AI message from the chat history
      const lastAIMessage = [...chatMessages].reverse().find(msg => msg.role === 'assistant');

      if (!lastAIMessage) {
        return;
      }

      setLoadingFollowUp(true);

      await openaiGenerateWithTextStreaming(
        'general_chat_bot_follow_up_question',
        {},
        [lastAIMessage],
        null,
        getFollowUpQuestionsCompleteAction,
        setLoadingFollowUp
      );
    } catch (error) {
      console.error('Error getting follow-up questions:', error);
      setLoadingFollowUp(false);
    }
  }, [chatMessages]);

  const sendMessage = async (messageText = inputMessage) => {
    try {
      setLoading(true);
      const existingMessages = [...chatMessages, { content: messageText, role: 'user' }];

      await openaiGenerateWithTextStreaming(
        promptIdentifier,
        {},
        existingMessages,
        null,
        result => setChatMessages([...existingMessages, { content: result, role: 'assistant' }]),
        setLoading
      );

      setInputMessage('');
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  const close = () => {
    clearConversation();
    toggleDialog('chatBot', { ...chatBotDialog, closed: true });
  };

  const onExited = () => {
    toggleDialog('chatBot', false);
  };

  useEffect(() => {
    if (!chatBotDialog) {
      clearConversation();
      setInputMessage('');
      setLoading(false);
    }
  }, [chatBotDialog, clearConversation]);

  useEffect(() => {
    const contentDiv = contentDivRef.current;
    if (contentDiv) {
      contentDiv.scrollTop = contentDiv.scrollHeight;
    }

    if (chatMessages && chatMessages.length > 0) {
      getFollowUpQuestions();
    }
  }, [chatMessages, getFollowUpQuestions]);

  const header = getTeamAiBotName(teamAiSettings, intl.formatMessage({ id: `${mode}-bot-dialog-title` }));

  const questionsSection = (
    <Grid style={{ width: '100%', marginBottom: 2 }}>
      {/* Show preset questions only when there are no messages */}
      {chatMessages.length === 0 && !loading && (
        <>
          <Typography>{intl.formatMessage({ id: 'preset-questions' })}</Typography>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              opacity: loading ? 0.6 : 1,
              pointerEvents: loading ? 'none' : 'auto'
            }}
          >
            {presetQuestions[mode].map(question => (
              <Button
                key={question.id}
                variant="outlined"
                color="primary"
                onClick={() => handlePresetQuestion(question.text)}
                disabled={loading}
                style={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  height: 'auto',
                  whiteSpace: 'normal',
                  textAlign: 'left',
                  lineHeight: 'unset',
                  margin: '6px',
                  minWidth: 'auto',
                  flex: '0 1 auto',
                  padding: '4px'
                }}
              >
                {question.text}
              </Button>
            ))}
          </div>
        </>
      )}

      {/* Show follow-up questions only after AI responds */}
      {!loadingFollowUp && chatMessages.length > 0 && followUpQuestions.length > 0 && (
        <>
          <Typography style={{ marginTop: 16 }}>{intl.formatMessage({ id: 'follow-up-questions' })}</Typography>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              opacity: loadingFollowUp ? 0.6 : 1,
              pointerEvents: loadingFollowUp ? 'none' : 'auto'
            }}
          >
            {followUpQuestions.map(question => (
              <Button
                key={question.id}
                variant="outlined"
                color="primary"
                onClick={() => handlePresetQuestion(question.text)}
                disabled={loading || loadingFollowUp}
                style={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  height: 'auto',
                  whiteSpace: 'normal',
                  textAlign: 'left',
                  lineHeight: 'unset',
                  margin: '6px',
                  minWidth: 'auto',
                  flex: '0 1 auto',
                  padding: '4px'
                }}
              >
                {question.text}
              </Button>
            ))}
          </div>
        </>
      )}
    </Grid>
  );

  const content = (
    <Grid
      container
      style={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}
      sx={{ flexDirection: 'column' }}
    >
      <Grid
        className="conversation-container"
        style={{
          width: '100%',
          flex: 1,
          overflow: 'auto',
          padding: '16px',
          height: 'calc(100% - 120px)'
        }}
        ref={contentDivRef}
      >
        <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
          {chatMessages.map((message, index) => (
            <Grid key={`${message.role}-${index}`} style={{ width: '100%' }}>
              <Typography align={message.role === 'assistant' ? 'left' : 'right'}>
                <strong>{message.role === 'assistant' && 'A.I. Pro:\n'}</strong>
                <div
                  className="markdown-content"
                  style={{
                    backgroundColor: message.role === 'assistant' ? '#3966f818' : 'transparent'
                  }}
                >
                  <Markdown className="markdown-content">{(message.content || '').trim()}</Markdown>
                </div>
              </Typography>
            </Grid>
          ))}
        </Grid>
        {questionsSection}
      </Grid>
      <Grid
        className="input-container"
        style={{
          width: '100%',
          padding: '16px',
          backgroundColor: '#fff'
        }}
      >
        <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-end' }}>
          <Grid style={{ width: '100%' }}>
            <TextField
              variant="outlined"
              value={inputMessage}
              onChange={onChangeInputMessage}
              onKeyPress={handleKeyPress}
              inputRef={inputRef}
              multiline
              rows={2}
              disabled={loading}
              style={{ width: '100%' }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => sendMessage()}
                        disabled={!inputMessage || loading}
                        edge="end"
                        size="large"
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon color="primary" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <BottomSheetOrDialog
      className="team-bot-dialog"
      open={!!chatBotDialog && !closed}
      onClose={close}
      onExited={onExited}
      header={header}
      content={content}
      BottomSheetProps={{
        snapPoints: ({ maxHeight }) => [maxHeight * 0.95],
        defaultSnap: ({ lastSnap, snapPoints }) => Math.max(...snapPoints),
        expandOnContentDrag: false,
        disableAutoDismiss: true,
        BoxProps: {
          height: 'calc(95vh - 54px - var(--top-padding))',
          padding: '0',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      DialogProps={{
        maxWidth: 'md',
        fullWidth: true,
        PaperProps: {
          style: {
            height: 'calc(95vh - 54px - var(--top-padding))',
            maxHeight: 'none'
          }
        }
      }}
      DialogParams={{
        dialogTitleCloseButton: true
      }}
    />
  );
};

const mapStateToProps = state => ({
  chatBotDialog: state.control.chatBotDialog,
  team: state.team.teams,
  teamShortName: state.team.teamShortName,
  teamAiSettings: state.team.aiSettings
});

export default withRouter(injectIntl(connect(mapStateToProps, { toggleDialog })(ChatBotDialog)));
